import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { MessageCircle, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useChamada } from "@/hooks/use-cargas";

/**
 * "Chamar produtores" — faltam N kg, quem tem banana?
 *
 * ⚠️ O app NÃO dispara mensagem. Ele monta o texto, o comprador toca e o
 * WhatsApp DELE abre. É a mesma decisão do envio da oferta (#210) e da nota do
 * produtor (C1): número de plataforma mandando mensagem comercial para
 * desconhecido é o padrão que o WhatsApp trata como maior risco de bloqueio.
 *
 * "Enviado" é marca LOCAL e otimista — serve para o comprador não se perder na
 * lista de doze produtores. Não vira estado no servidor: quem envia é ele, e o
 * app não tem como saber se a mensagem saiu de verdade.
 *
 * O TEXTO vem do servidor (`GET /cargas/:id/chamada`), inteiro e já formatado.
 * Montá-lo aqui daria a segunda versão do mesmo texto, e a primeira divergência
 * apareceria num número com separador de milhar errado.
 */
export function PainelChamadaCarga({
  cargaId,
  aberta,
  onFechar,
}: {
  cargaId: string;
  aberta: boolean;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const { data, isLoading } = useChamada(cargaId, aberta);
  const [enviados, setEnviados] = useState<string[]>([]);

  async function copiar(texto: string) {
    try {
      await navigator.clipboard.writeText(texto);
      toast({ title: "Texto copiado", description: "Cole no grupo da praça." });
    } catch (e: any) {
      // Nunca `catch {}`: sem permissão de clipboard o comprador precisa saber
      // que nada foi copiado — senão ele cola o que estava na área antes.
      toast({ title: "Não consegui copiar", description: e?.message, variant: "destructive" });
    }
  }

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Chamar produtores</DialogTitle>
          <DialogDescription>
            {data?.faltam != null
              ? `Faltam ${data.faltam.toLocaleString("pt-BR")} kg para fechar esta carga.`
              : "Sem alvo definido, a chamada não diz quanto falta."}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Montando a mensagem…</p>
        ) : (
          <div className="space-y-4">
            {/* `whitespace-pre-wrap`: as linhas em branco separam conversa de
                romaneio no WhatsApp, e sem isto o navegador as colapsa. */}
            <pre className="whitespace-pre-wrap rounded-lg border bg-muted/40 p-3 text-sm font-sans" data-testid="texto-chamada">
              {data.texto}
            </pre>

            <Button variant="outline" className="w-full" onClick={() => copiar(data.texto)} data-testid="button-copiar-chamada">
              <Copy className="h-4 w-4 mr-1" />Copiar texto
            </Button>

            {data.links.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum produtor da sua carteira tem WhatsApp cadastrado. Copie o texto e mande no grupo da praça.
              </p>
            ) : (
              <ul className="divide-y max-h-72 overflow-y-auto" data-testid="lista-chamada">
                {data.links.map(link => {
                  const enviado = enviados.indexOf(link.contatoId) >= 0;
                  return (
                    <li key={link.contatoId} className="py-2 flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-sm">{link.nome}</span>
                      <Button
                        variant={enviado ? "ghost" : "outline"}
                        size="sm"
                        className="shrink-0"
                        asChild
                        onClick={() => setEnviados(prev => prev.indexOf(link.contatoId) >= 0 ? prev : prev.concat([link.contatoId]))}
                      >
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`link-chamada-${link.contatoId}`}
                        >
                          {enviado
                            ? <><Check className="h-4 w-4 mr-1" />Enviado</>
                            : <><MessageCircle className="h-4 w-4 mr-1" />Enviar</>}
                        </a>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
